<script setup lang="ts">
import type { RequireAtLeastOne } from 'type-fest';

import { reactive, ref, toRaw, watch } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import KNumberField from '@/components/fields/KNumberField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import useForm from '@/composables/useForm';
import { getMinLengthValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  suburbGuess?: string | null;
  postCode?: number | null;
}

// for submit try to avoid null (https://github.com/sindresorhus/meta/discussions/7) and use RequireAtLeastOne
// TODO its needed to clear values from nulls and empty arrays before submit
export interface _ValidData {
  suburbGuess?: string;
  postCode?: number;
}

export type ValidData = RequireAtLeastOne<
  _ValidData,
  'suburbGuess' | 'postCode'
>;

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

watch(formData, async (_formData) => {
  if (form.value === null) return;
  // TODO debounce, switch/dissapreance of multiple validators
  await form.value.validate();
});

function customRequiredValidationRule(): boolean | string {
  if (!formData.suburbGuess?.length && !formData.postCode?.toString().length) {
    return 'At least one is required';
    // return requiredValidationRule(value);
  }
  return true;
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.suburbsSearchSearchForm"
    data-testid="suburbsSearchSearchForm"
    @submit.prevent="handleSubmit"
  >
    <KTextField
      v-model="formData.suburbGuess"
      size="lg"
      label="Suburb name"
      :disabled="props.loading"
      :rules="[getMinLengthValidationRule(3), customRequiredValidationRule]"
      :class="$style.field"
      clearable
    />
    <KNumberField
      v-model="formData.postCode"
      size="lg"
      label="Postcode"
      :min="0"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      clearable
    />
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.suburbsSearchSearchForm {
}
  .field {
    width: 17.5rem;
    display: inline-block;
    vertical-align: top;
  }
  .field {
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .submit {
    margin-bottom: calc(0.5rem + 22px);
  }
</style>
